import { useColumnDescriptionStore } from "stores/useColumnDescriptionStore";
import { useCheckpointsStore } from "stores/useCheckpointsStore";
import { useColumnPropertiesStore } from "stores/useColumnPropertiesStore";
import { useLinkedColumnsStore } from "stores/useLinkedColumnsStore";
import { useBoundStore } from "@/components/canvas/stores/useAppStore";
import {
  useFilterStore,
  type FilterStore,
} from "@/components/canvas/stores/FilterStore";

/** The bar's ↑, which Commit's type-change question hangs from. */
export const commitSlot = () =>
  document.querySelector<HTMLElement>(
    '.canvas-action-toolbar__slot[data-family="commit"]',
  );

/** Hung from ↑, the question is the bar's like any overlay a slot opened, so a
 *  selection change takes it away. */
const withAnchor = <T extends object>(confirm: T | null) =>
  confirm && { ...confirm, anchor: commitSlot() };

/** Commit's type-change question, in the shape of an overlay's store. */
const typeChangeStore = {
  getState: () => ({
    open: withAnchor(useBoundStore.getState().typeChangeConfirm),
    close: () => useBoundStore.getState().setTypeChangeConfirm(null),
  }),
};

/** The view whose filter is up from the bar: in the editor, or pressed to read. */
const filterViewOf = (s: FilterStore) =>
  s.filterDialogOpenViewId ?? (s.peek?.pinned ? s.peek.viewId : null);
const filterOpen = (viewId: string | null) => (viewId ? { viewId } : null);

/** A view's filter dialog, in the shape of an overlay's store. */
const filterStore = {
  getState: () => ({
    open: filterOpen(filterViewOf(useFilterStore.getState())),
    close: () => useFilterStore.getState().closeDialogAndReset(),
  }),
};

/** The overlays a bar member opens but the bar does not draw. */
const STORES = {
  description: useColumnDescriptionStore,
  checkpoints: useCheckpointsStore,
  properties: useColumnPropertiesStore,
  links: useLinkedColumnsStore,
  typeChange: typeChangeStore,
  filter: filterStore,
};

export type OverlayKey = keyof typeof STORES;

export type OverlayOpens = {
  [K in OverlayKey]: ReturnType<(typeof STORES)[K]["getState"]>["open"];
};

/** Each overlay's open payload, re-rendering on any change. */
export function useOverlayOpens(): OverlayOpens {
  const typeChange = useBoundStore((s) => s.typeChangeConfirm);
  const filterViewId = useFilterStore(filterViewOf);
  return {
    description: useColumnDescriptionStore((s) => s.open),
    checkpoints: useCheckpointsStore((s) => s.open),
    properties: useColumnPropertiesStore((s) => s.open),
    links: useLinkedColumnsStore((s) => s.open),
    typeChange: withAnchor(typeChange),
    filter: filterOpen(filterViewId),
  };
}

export const closeOverlay = (key: OverlayKey): void =>
  STORES[key].getState().close();
